import { hasValue } from '@repo/utils';

import type { TCtcRowResponse } from '../../../types/calculate-ctc';
import type { TGraphTrajectoryView } from '../../../types/graph-trajectory';
import { REFERENCE_TRAJECTORY_LINE_WIDTH, getTrajectoryLineColor } from '../../../utils/trajectory-line-colors';
import { formatGraphTrajectoryLegendLabel } from '../build-graph-trajectory-views';

import type {
  Trajectory2dChartPoint,
  Trajectory2dMappedChart,
  Trajectory2dSeriesData,
} from './map-trajectory-points-by-config';

export const EMPTY_CTC_MAPPED_CHART: Trajectory2dMappedChart = {
  series: [],
  excluded: [],
  yInvert: false,
  yLabelKey: 'TableHeadersShort.centrToCentr',
};

function sortPointsByActiveMd(left: Trajectory2dChartPoint, right: Trajectory2dChartPoint): number {
  return left[0] - right[0];
}

function toCrsExcluded(trajectory: TGraphTrajectoryView, activeLegendLabel: string) {
  return {
    trajectoryId: trajectory.id,
    legendLabel: formatGraphTrajectoryLegendLabel(trajectory, activeLegendLabel),
    reason: 'crsMismatch' as const,
  };
}

export function mapCtcRowsToChart(
  rows: readonly TCtcRowResponse[],
  trajectories: readonly TGraphTrajectoryView[],
  crsExcludedTrajectories: readonly TGraphTrajectoryView[],
  activeLegendLabel: string,
  errorCode: string | null,
): Trajectory2dMappedChart {
  const frontendExcluded = crsExcludedTrajectories.map((trajectory) => toCrsExcluded(trajectory, activeLegendLabel));

  if (errorCode === 'INCOMPATIBLE_CRS') {
    const requested = trajectories.filter((item) => item.role === 'reference');

    return {
      ...EMPTY_CTC_MAPPED_CHART,
      excluded: [...frontendExcluded, ...requested.map((trajectory) => toCrsExcluded(trajectory, activeLegendLabel))],
    };
  }

  const viewsById = new Map(trajectories.map((item) => [item.id, item]));
  const pointsByOffset = new Map<number, Trajectory2dChartPoint[]>();

  for (const row of rows) {
    const offsetId = row.offsetTrajectoryId;
    const x = row.activeMd;
    const y = row.centrToCentr;

    if (!hasValue(offsetId) || !hasValue(x) || !hasValue(y)) {
      continue;
    }

    const points = pointsByOffset.get(offsetId) ?? [];
    points.push([x, y]);
    pointsByOffset.set(offsetId, points);
  }

  const series: Trajectory2dSeriesData[] = [...pointsByOffset.entries()].map(([offsetId, data]) => {
    const view = viewsById.get(offsetId);
    const legendLabel = (() => {
      if (view) {
        return formatGraphTrajectoryLegendLabel(view, activeLegendLabel);
      }

      return String(offsetId);
    })();

    return {
      trajectoryId: offsetId,
      trajectoryTitle: view?.title ?? String(offsetId),
      legendLabel,
      field: 'md',
      labelKey: 'TableHeadersShort.md',
      axisIndex: 0,
      color: view?.color ?? getTrajectoryLineColor(offsetId),
      lineWidth: REFERENCE_TRAJECTORY_LINE_WIDTH,
      data: [...data].sort(sortPointsByActiveMd),
    };
  });

  return {
    ...EMPTY_CTC_MAPPED_CHART,
    series,
    excluded: frontendExcluded,
  };
}
